/* eslint-disable */
/**
 * Remove Secondary Criteria Validation Functions
 * Contains all validation logic for Remove Secondary Criteria Excel uploads
 */

import { isValidInput, isNotValidInput, transformText, appendComment, tranformDate } from './validationUtils';
import {
  RemoveSecondaryCriteriaIndices,
  ValidationReferenceData,
  ExcelData,
  ValidationStatusFlagSetter
} from './validationTypes';

/**
 * Main validation function for Remove Secondary Criteria Excel data
 * 
 * @param data - Excel data array (2D array with headers in row 0)
 * @param referenceData - Reference data for validation (rule types, criteria types, etc.)
 * @param EXCEL_HEADER_RMVSC_INDEX - Column indices for Remove Secondary Criteria
 * @param validationStatusFlagSetter - Function to set validation status flag
 * @returns Promise resolving to validated data array
 */
export const validateRemoveSecondaryCriteria = async (
  data: ExcelData,
  referenceData: ValidationReferenceData,
  EXCEL_HEADER_RMVSC_INDEX: RemoveSecondaryCriteriaIndices,
  validationStatusFlagSetter: ValidationStatusFlagSetter
): Promise<ExcelData> => {
      // Validation function for Remove Secondary Criteria excel uploads
      console.log('in validate Remove Secondary Criteria')
      
      // Destructure column indices
      const {
        ZONE_RMVSC_IND, DEPARTMENT_RMVSC_IND, SUB_DEPARTMENT_RMVSC_IND, BRACKET_RMVSC_IND,
        RMS_RMVSC_IND, BRAND_RMVSC_IND, RULE_TYPE_RMVSC_IND, CRITERIA_TYPE_RMVSC_IND,
        PRICE_PARAM_RMVSC_IND, EFFECT_TYPE_RMVSC_IND, RULE_APP_ORDER_RMVSC_IND,
        CATEGORY_TYPE_RMVSC_IND, CATEGORY_SUBTYPE_RMVSC_IND, EQUATION_TYPE_RMVSC_IND,
        START_DATE_RMVSC_IND, END_DATE_RMVSC_IND, VALIDATION_STATUS_RMVSC_IND,
        VALIDATION_COMMENT_RMVSC_IND, UPLOAD_STATUS_RMVSC_IND, UPLOAD_COMMENTS_RMVSC_IND
      } = EXCEL_HEADER_RMVSC_INDEX;
      
  
      let validationFlag = true;
  
      // Expected columns for Remove Secondary Criteria:
      // Zone, Department, Sub-Department, Bracket, RMS Number, Brand, Rule Type, 
      // Secondary Criteria Type, Price Parameter, Criteria Effect Type, Rule Application Order,
      // Category Type, Category Subtype, Equation Type, From Range, To Range
  
  
      // Transform dates for all rows first
      for (let rowindex = 1; rowindex < data.length; rowindex++) {
        if (data[rowindex].length == 0) {
          break;
        }
  
        // Transform Start Date if it's a number (Excel serial date)
        if (isValidInput(data[rowindex][START_DATE_RMVSC_IND])) {
          const startValue = data[rowindex][START_DATE_RMVSC_IND];
          // Only transform if it's a valid positive number (Excel serial date)
          if (typeof startValue === 'number' && !isNaN(startValue) && startValue > 0) {
            const startResult = tranformDate(startValue);
            data[rowindex][START_DATE_RMVSC_IND] = startResult;
          }
          // If it's a string, leave it as-is - validation will catch invalid strings later
        }
  
        // Transform End Date if it's a number (Excel serial date)
        if (isValidInput(data[rowindex][END_DATE_RMVSC_IND])) {
          const endValue = data[rowindex][END_DATE_RMVSC_IND];
          // Only transform if it's a valid positive number (Excel serial date)
          if (typeof endValue === 'number' && !isNaN(endValue) && endValue > 0) {
            const endResult = tranformDate(endValue);
            data[rowindex][END_DATE_RMVSC_IND] = endResult;
          }
          // If it's a string, leave it as-is - validation will catch invalid strings later
        }
      }
  
      // Duplicate validation - check for duplicate records after date transformation
      const seenRows = new Set<string>();
  
      for (let rowindex = 1; rowindex < data.length; rowindex++) {
        if (data[rowindex].length == 0) {
          break;
        }
  
        const row = data[rowindex];
        const rowIdent = [
          row[ZONE_RMVSC_IND],
          row[DEPARTMENT_RMVSC_IND],
          row[SUB_DEPARTMENT_RMVSC_IND],
          row[BRACKET_RMVSC_IND],
          row[RMS_RMVSC_IND],
          row[BRAND_RMVSC_IND],
          row[RULE_TYPE_RMVSC_IND],
          row[CRITERIA_TYPE_RMVSC_IND],
          row[PRICE_PARAM_RMVSC_IND],
          row[EFFECT_TYPE_RMVSC_IND],
          row[RULE_APP_ORDER_RMVSC_IND],
          row[CATEGORY_TYPE_RMVSC_IND],
          row[CATEGORY_SUBTYPE_RMVSC_IND],
          row[EQUATION_TYPE_RMVSC_IND],
          row[START_DATE_RMVSC_IND],
          row[END_DATE_RMVSC_IND]
        ].map(value => (typeof value === 'string' ? value.trim() : value))
          .filter(value => value !== undefined && value !== null && value !== '')
          .join('|');
  
        if (rowIdent != '') {
          if (seenRows.has(rowIdent)) {
            row[VALIDATION_STATUS_RMVSC_IND] = 'Failed';
            row[VALIDATION_COMMENT_RMVSC_IND] = appendComment(row[VALIDATION_COMMENT_RMVSC_IND], 'Duplicate records found');
            validationFlag = false;
          } else {
            seenRows.add(rowIdent);
          }
        }
      }
  
      for (let rowindex = 1; rowindex < data.length; rowindex++) {
        if (data[rowindex].length == 0) {
          break;
        }
  
        // Skip duplicate records - they've already been marked as failed
        if (data[rowindex][VALIDATION_STATUS_RMVSC_IND] === 'Failed' && 
            data[rowindex][VALIDATION_COMMENT_RMVSC_IND]?.includes('Duplicate records found')) {
          continue;
        }
  
        // Initialize validation status
        data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Passed';
        data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = undefined;
        data[rowindex][UPLOAD_STATUS_RMVSC_IND] = undefined;
        data[rowindex][UPLOAD_COMMENTS_RMVSC_IND] = undefined;
  
        // Zone Validation - Zone is mandatory
        if (isNotValidInput(data[rowindex][ZONE_RMVSC_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = "Zone is mandatory";
          continue;
        }
  
        // Validate Zone is a positive integer (no decimal, no negative)
        if (isNaN(data[rowindex][ZONE_RMVSC_IND]) ||
          data[rowindex][ZONE_RMVSC_IND] < 0 ||
          data[rowindex][ZONE_RMVSC_IND].toString().includes('.')) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
  
          if (isNaN(data[rowindex][ZONE_RMVSC_IND])) {
            data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = "Invalid Zone number";
          } else if (data[rowindex][ZONE_RMVSC_IND] < 0) {
            data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = "Zone number should not be less than zero";
          } else if (data[rowindex][ZONE_RMVSC_IND].toString().includes('.')) {
            data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = "Zone number should not be a decimal number";
          }
          continue;
        }
  
        // RMS Hierarchy Validation
        // At least one hierarchy level is required
        if (isNotValidInput(data[rowindex][DEPARTMENT_RMVSC_IND]) &&
          isNotValidInput(data[rowindex][SUB_DEPARTMENT_RMVSC_IND]) &&
          isNotValidInput(data[rowindex][BRACKET_RMVSC_IND]) &&
          isNotValidInput(data[rowindex][RMS_RMVSC_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = "Hierarchy information is required. Please provide the following data: Department (mandatory), Sub-department (optional), Bracket (optional), and RMS Number (optional)";
          continue;
        }
  
        // If Sub-Department is provided, Department must exist
        if (isValidInput(data[rowindex][SUB_DEPARTMENT_RMVSC_IND]) && isNotValidInput(data[rowindex][DEPARTMENT_RMVSC_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Department is required when Sub-Department is provided");
        }
  
        // If Bracket is provided, both Department and Sub-Department must exist
        if (isValidInput(data[rowindex][BRACKET_RMVSC_IND]) &&
          (isNotValidInput(data[rowindex][DEPARTMENT_RMVSC_IND]) || isNotValidInput(data[rowindex][SUB_DEPARTMENT_RMVSC_IND]))) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Department and Sub-Department are required when Bracket is provided");
        }
  
        // If RMS Item is provided, Department, Sub-Department, and Bracket should NOT be present
        if (isValidInput(data[rowindex][RMS_RMVSC_IND]) &&
          (isValidInput(data[rowindex][DEPARTMENT_RMVSC_IND]) ||
            isValidInput(data[rowindex][SUB_DEPARTMENT_RMVSC_IND]) ||
            isValidInput(data[rowindex][BRACKET_RMVSC_IND]))) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "RMS Number does not require other hierarchy level information");
        }
  
        // Validate numeric hierarchy fields
        if (isValidInput(data[rowindex][DEPARTMENT_RMVSC_IND]) && isNaN(data[rowindex][DEPARTMENT_RMVSC_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Department is invalid");
        }
  
        if (isValidInput(data[rowindex][SUB_DEPARTMENT_RMVSC_IND]) && isNaN(data[rowindex][SUB_DEPARTMENT_RMVSC_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Sub-Department is invalid");
        }
  
        if (isValidInput(data[rowindex][BRACKET_RMVSC_IND]) && isNaN(data[rowindex][BRACKET_RMVSC_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Bracket is invalid");
        }
  
        if (isValidInput(data[rowindex][RMS_RMVSC_IND]) && isNaN(data[rowindex][RMS_RMVSC_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "RMS Number is invalid");
        }

    // Validate Brand - alphanumeric with spaces allowed (but not at beginning or end)
    // Brand is optional, but if provided must be alphanumeric with optional spaces between words
    if (isValidInput(data[rowindex][BRAND_RMVSC_IND])) {
      const brandValue = data[rowindex][BRAND_RMVSC_IND].toString();
      // Check if Brand contains only letters, numbers, and spaces
      if (!/^[a-zA-Z0-9 ]+$/.test(brandValue)) {
            validationFlag = false;
            data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
            data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Brand should contain only alphanumeric characters");
          }
        }
  
        // Validate Rule Type is mandatory
        if (isNotValidInput(data[rowindex][RULE_TYPE_RMVSC_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Rule Type is mandatory");
        } else {
          //Validate Rule Type against reference data (DB validation)
          if (!referenceData.ruleApplicationType.includes(transformText(data[rowindex][RULE_TYPE_RMVSC_IND]))) {
            validationFlag = false;
            data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
            data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Rule Type is invalid");
          } else {
            // Rule Type Specific Field Validation
            const ruleType = transformText(data[rowindex][RULE_TYPE_RMVSC_IND]);
  
            // If Rule Type is "table", category and equation fields should not be provided
            if (ruleType === 'table') {
              if (isValidInput(data[rowindex][CATEGORY_TYPE_RMVSC_IND])) {
                validationFlag = false;
                data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
                data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Category Type is not allowed when Rule Type is 'TABLE'");
              }
              if (isValidInput(data[rowindex][CATEGORY_SUBTYPE_RMVSC_IND])) {
                validationFlag = false;
                data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
                data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Category Subtype is not allowed when Rule Type is 'TABLE'");
              }
              if (isValidInput(data[rowindex][EQUATION_TYPE_RMVSC_IND])) {
                validationFlag = false;
                data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
                data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Equation Type is not allowed when Rule Type is 'TABLE'");
              }
            }
  
            // If Rule Type is "equation", category fields should not be provided, but Equation Type is mandatory
            if (ruleType === 'equation') {
              // Equation Type is mandatory for EQUATION rule type
              if (isNotValidInput(data[rowindex][EQUATION_TYPE_RMVSC_IND])) {
                validationFlag = false;
                data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
                data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Equation Type is mandatory when Rule Type is 'EQUATION'");
              }
              
              if (isValidInput(data[rowindex][CATEGORY_TYPE_RMVSC_IND])) {
                validationFlag = false;
                data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
                data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Category Type is not allowed when Rule Type is 'EQUATION'");
              }
              if (isValidInput(data[rowindex][CATEGORY_SUBTYPE_RMVSC_IND])) {
                validationFlag = false;
                data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
                data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Category Subtype is not allowed when Rule Type is 'EQUATION'");
              }

          // Validate Equation Type against reference data (DB validation)
          if (isValidInput(data[rowindex][EQUATION_TYPE_RMVSC_IND])) {
            if (!referenceData.equationType.includes(transformText(data[rowindex][EQUATION_TYPE_RMVSC_IND]))) {
              validationFlag = false;
              data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
              data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Equation Type is invalid");
            }
          }
        }

        // If Rule Type is "category", equation fields should not be provided, but Category Type and Category Subtype are mandatory
            if (ruleType === 'category') {
              // Category Type is mandatory for CATEGORY rule type
              if (isNotValidInput(data[rowindex][CATEGORY_TYPE_RMVSC_IND])) {
                validationFlag = false;
                data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
                data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Category Type is mandatory when Rule Type is 'CATEGORY'");
              }
              
              // Category Subtype is also mandatory for CATEGORY rule type
              if (isNotValidInput(data[rowindex][CATEGORY_SUBTYPE_RMVSC_IND])) {
                validationFlag = false;
                data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
                data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Category Subtype is mandatory when Rule Type is 'CATEGORY'");
              }
              
              if (isValidInput(data[rowindex][EQUATION_TYPE_RMVSC_IND])) {
                validationFlag = false;
                data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
                data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Equation Type is not allowed when Rule Type is 'CATEGORY'");
        }

          // Validate Category Type against reference data (DB validation)
          if (isValidInput(data[rowindex][CATEGORY_TYPE_RMVSC_IND])) {
            if (!referenceData.categoryType.includes(transformText(data[rowindex][CATEGORY_TYPE_RMVSC_IND]))) {
              validationFlag = false;
              data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
              data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Category Type is invalid");
            }
          }

          // Validate Category Subtype against reference data (DB validation)
          if (isValidInput(data[rowindex][CATEGORY_SUBTYPE_RMVSC_IND])) {
            if (!referenceData.categorySubType.includes(transformText(data[rowindex][CATEGORY_SUBTYPE_RMVSC_IND]))) {
              validationFlag = false;
              data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
              data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Category Subtype is invalid");
            }
          }
        }
      }
    }

    // Validate Secondary Criteria Type is mandatory
    if (isNotValidInput(data[rowindex][CRITERIA_TYPE_RMVSC_IND])) {
      validationFlag = false;
      data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
      data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Secondary Criteria Type is mandatory");
    } // Validate Secondary Criteria Type against reference data (DB validation)
    else {
      if (!referenceData.secondaryCriteriaType.includes(transformText(data[rowindex][CRITERIA_TYPE_RMVSC_IND]))) {
        validationFlag = false;
        data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Secondary Criteria Type is invalid");
      }
    }

    // Validate Price Parameter is mandatory
    if (isNotValidInput(data[rowindex][PRICE_PARAM_RMVSC_IND])) {
      validationFlag = false;
      data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
      data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Price Parameter is mandatory");
    }// Validate Price Parameter against reference data (DB validation)
    else {
      if (!referenceData.pricingParamKey.includes(transformText(data[rowindex][PRICE_PARAM_RMVSC_IND]))) {
        validationFlag = false;
        data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Price Parameter is invalid");
      }
    }

    // Validate Rule Application Order is optional, but if provided must be a number starting from 1
    if (isValidInput(data[rowindex][RULE_APP_ORDER_RMVSC_IND])) {
      if (isNaN(data[rowindex][RULE_APP_ORDER_RMVSC_IND]) ||
        data[rowindex][RULE_APP_ORDER_RMVSC_IND] < 1 ||
        data[rowindex][RULE_APP_ORDER_RMVSC_IND].toString().includes('.')) {
            validationFlag = false;
            data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
            data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "Rule Application Order is invalid");
          }
        }
  
        // Validate Start Date - MANDATORY
        // Note: Date transformation already done at the beginning of the function
        if (isNotValidInput(data[rowindex][START_DATE_RMVSC_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "From Range is mandatory");
        } else {
          // Check if it's a Date object (successfully transformed)
          if (data[rowindex][START_DATE_RMVSC_IND] instanceof Date) {
            // Date is valid and transformed - no additional validation needed for Remove SC
            // Remove Secondary Criteria allows past start dates (no past date validation)
          } else {
            // If it's not a Date object, it means transformation failed - invalid date
            validationFlag = false;
            data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
            data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "From Range is invalid");
          }
        }
  
        // Validate End Date - MANDATORY
        // Note: Date transformation already done at the beginning of the function
        if (isNotValidInput(data[rowindex][END_DATE_RMVSC_IND])) {
          validationFlag = false;
          data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "To Range is mandatory");
        } else {
          // Check if it's a Date object (successfully transformed)
          if (data[rowindex][END_DATE_RMVSC_IND] instanceof Date) {
            // Date is valid and transformed, now validate business rules
            const endResult = data[rowindex][END_DATE_RMVSC_IND];
  
            // Validate end date is not in the past (if feature flag requires it)
              const uscurrentdate = new Date(new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' })).setHours(0, 0, 0, 0));
              if (new Date(uscurrentdate.toDateString()) > new Date(endResult.toDateString())) {
                validationFlag = false;
                data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
                data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "To Range should not contain past dates");
        }

            // Validate end date is after or equal to start date if both are provided
            if (isValidInput(data[rowindex][START_DATE_RMVSC_IND]) && data[rowindex][START_DATE_RMVSC_IND] instanceof Date) {
              const startDate = new Date(data[rowindex][START_DATE_RMVSC_IND]);
              const endDate = new Date(endResult);
              if (endDate < startDate) {
                validationFlag = false;
                data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
                data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "To Range should be greater than or equal to From Range");
              }
            }
          } else {
            // If it's not a Date object, it means transformation failed - invalid date
            validationFlag = false;
            data[rowindex][VALIDATION_STATUS_RMVSC_IND] = 'Failed';
            data[rowindex][VALIDATION_COMMENT_RMVSC_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVSC_IND], "To Range is invalid");
          }
        }
      }
  
      if (validationFlag) {
        validationStatusFlagSetter('Validation Passed');
      } else {
        validationStatusFlagSetter('Validation Failed');
      }
  
      console.log('Remove Secondary Criteria validation completed. Status:', validationFlag ? 'Validation Passed' : 'Validation Failed');
      return data;
};
